/**
 * Escopo de Cliente/Projeto dos seletores do RDO.
 *
 * A camada de apresentação usa 'cli-default' como sentinela quando nenhum
 * cliente está selecionado. Esse valor é reconhecido pelo repositório de RDO,
 * mas NÃO é um cliente: filtrar Projetos ou Frentes por ele devolve lista
 * vazia. Estas funções puras isolam essa regra para que ela não volte a se
 * perder dentro de uma página.
 */

/** Sentinela emitido pela apresentação quando não há cliente selecionado. */
export const NO_CLIENT_SENTINEL = "cli-default";

/**
 * Cliente real para filtrar cadastros, ou undefined quando não há nenhum —
 * e undefined significa "listar tudo", nunca "listar nada".
 */
export function resolveClientScope(activeClientId: string | null | undefined): string | undefined {
  const value = (activeClientId ?? "").trim();
  if (!value) return undefined;
  if (value === NO_CLIENT_SENTINEL) return undefined;
  return value;
}

export interface ProjectOption {
  id: string;
  code: string;
  name: string;
  clientId: string;
  status?: string;
}

/**
 * Projetos oferecidos no seletor.
 *
 * Sem cliente no escopo, o cadastro inteiro é oferecido — a ausência de
 * contexto global não pode esvaziar a lista. O status é comparado com o valor
 * real do domínio ('ACTIVE'); projeto sem status gravado continua visível, em
 * vez de sumir por causa de um campo ausente.
 */
export function projectOptionsForScope(
  projects: readonly ProjectOption[],
  clientId: string | undefined,
  options: { onlyActive?: boolean } = {}
): ProjectOption[] {
  const scoped = clientId ? projects.filter(project => project.clientId === clientId) : [...projects];
  const visible = options.onlyActive === false
    ? scoped
    : scoped.filter(project => !project.status || project.status === "ACTIVE");

  return visible.sort((a, b) => `${a.code} ${a.name}`.localeCompare(`${b.code} ${b.name}`, "pt-BR", { numeric: true }));
}

/** Rótulo do seletor: CÓDIGO — NOME DO PROJETO. */
export function formatProjectLabel(project: ProjectOption): string {
  return project.code ? `${project.code} — ${project.name}` : project.name;
}
